import {
  mapCapacityAllocationFromApi,
  mapCapacityListFromApi,
  mapCapacityPOST,
  mapCapacityPATCH,
  mapAllocationPATCH,
  mapDispatchAllocationPOST,
  mapConfigOperationBalanceListFromApi,
  mapCapacityStatePATCH,
  mapCapacityFromApi
} from './capacity.mapper'
import {
  patchStateOperation,
  apiRepartosGET,
  apiRepartosPATCH
} from '../../api/oData/endpoints'
import { apiFetchLogisticElementsSelectOptionsBySubtypes } from '../../api/Pioneer/endpoints/logisticElements/logisticElements.api'
import { CapacityModel, AllocationModel } from './capacity.model'
import {
  EnumBloqueoCapacidad,
  EnumServicio,
  EnumOperationState,
  EnumDispatchType,
  EnumTipoNaturaleza,
  EnumTipoProductoCapacidad
} from '../../api/oData/enums'
import { SelectOption } from '@globaleris/ui.library'

import { enumKeyToValue, enumValueToKey, enumEnumToValueString } from '../../helpers'
import { DataTableActionConfiguration, ActionIcons } from '../../model'
import i18n from '../../i18n'
import { apiMassiveDispatchesPOST } from '../../api/Pioneer/endpoints/dispatches'
import { apiConfigServicioCapacidadGET } from '../../api/oData/endpoints/configServiciosCapacidad'
import { getActiveBalanceZone, getActiveCompany, notifyError } from '../../redux'
import {
  createErrorServiceResponse,
  createSuccessServiceResponse
} from '../../helpers/serviceResponseHelper'
import { ServiceResponse } from '../../model/serviceResponse'
import moment from 'moment'
import {
  getCapacityByID,
  postCapacityChangeLock,
  getCapacitiesActivas,
  getCapacities,
  postCapacity,
  patchCapacity
} from '../../api/Pioneer/endpoints/capacities/capacities.api'
import { EnumLockCapacity } from '../../api'

export const servicesFilter = [
  EnumServicio.AlmacenamientoGNL,
  EnumServicio.AlmacenamientoPVB,
  EnumServicio.CapacidadAlmacenamiento,
  EnumServicio.CargaBuque,
  EnumServicio.CargaCisterna,
  EnumServicio.DescargaBuque,
  EnumServicio.GrupoDistribucion1Cliente,
  EnumServicio.GrupoDistribucion1Transportista,
  EnumServicio.GrupoDistribucion2Cliente,
  EnumServicio.GrupoDistribucionAlta,
  EnumServicio.GrupoDistribucion3Cliente,
  EnumServicio.GrupoDistribucionBaja,
  EnumServicio.EntradaRT,
  EnumServicio.Inyeccion,
  EnumServicio.Extraccion,
  EnumServicio.Licuefaccion,
  EnumServicio.Regasificacion,
  EnumServicio.SalidaCI,
  EnumServicio.DistribucionClienteFinal,
  EnumServicio.DistribucionDistribuidor
]

export const fetchTodayActiveCapacities = async (
  startDate: Date,
  endDate: Date
): Promise<ServiceResponse<any>> => {
  return await getCapacitiesActivas(
    startDate,
    endDate,
    getActiveBalanceZone(),
    getActiveCompany()
  )
    .then(response => {
      return createSuccessServiceResponse(mapCapacityListFromApi(response))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchDateRangeActiveCapacities = async (
  startDate: Date,
  endDate: Date,
  service?: number,
  logisticElement?: number,
  duration?: number,
  nature?: number
): Promise<ServiceResponse<any>> => {
  return await getCapacities(
    startDate,
    endDate,
    getActiveBalanceZone(),
    getActiveCompany(),
    enumKeyToValue(EnumServicio, service),
    logisticElement,
    enumKeyToValue(EnumTipoProductoCapacidad, duration),
    enumKeyToValue(EnumTipoNaturaleza, nature)
  )
    .then(response => {
      return createSuccessServiceResponse(mapCapacityListFromApi(response))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const updateLockState = async (
  id: number,
  newLockState: EnumBloqueoCapacidad
): Promise<boolean> => {
  const newState = enumEnumToValueString(
    EnumLockCapacity,
    newLockState
  ) as unknown as EnumLockCapacity
  if (!newState) {
    return false
  }
  return await postCapacityChangeLock(id, newState)
    .then(() => {
      return true
    })
    .catch(() => {
      notifyError(i18n.t('capacity:notifications.error.updateLockState'))
      return false
    })
}

export const getCapacityItem = async (idCapacity: number): Promise<ServiceResponse<any>> => {
  return await getCapacityByID(idCapacity)
    .then(async response => {
      return apiRepartosGET(idCapacity)
        .then(allocations => {
          return createSuccessServiceResponse(mapCapacityAllocationFromApi(response, allocations))
        })
        .catch(error => {
          return createErrorServiceResponse(error)
        })
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}
export const add = async (item: CapacityModel): Promise<CapacityModel | null> => {
  return await postCapacity(
    mapCapacityPOST(item, getActiveBalanceZone()?.id, getActiveCompany()?.id)
  )
    .then(response => {
      return mapCapacityFromApi(response)
    })
    .catch(() => {
      notifyError(i18n.t('capacity:notifications.error.create'))
      return null
    })
}

export const patchCapacityItem = async (item: CapacityModel): Promise<boolean> => {
  return await patchCapacity(mapCapacityPATCH(item))
    .then(() => {
      return true
    })
    .catch(() => {
      notifyError(i18n.t('capacity:notifications.error.edit'))
      return false
    })
}

export const sendAllocations = async (allocations: AllocationModel[]): Promise<boolean> => {
  return await apiMassiveDispatchesPOST(
    mapDispatchAllocationPOST(
      allocations.filter(item => {
        if (
          enumValueToKey(EnumOperationState, item.operationState) ===
          enumValueToKey(EnumOperationState, EnumOperationState.PendienteRenvio)
        ) {
          return true
        }

        return false
      }),
      EnumDispatchType.Reparto03G,
      getActiveCompany()?.id!
    )
  )
    .then(() => {
      return true
    })
    .catch(() => {
      notifyError(i18n.t('capacity:notifications.error.sendAllocations'))
      return false
    })
}

export const fetchLogisticElementsToItems = async (
  serviceTypes: EnumServicio[]
): Promise<SelectOption[]> => {
  return await fetchLogisticElements(serviceTypes)
}

export const fetchLogisticElements = async (
  serviceTypes: EnumServicio[]
): Promise<SelectOption[]> => {
  return await apiFetchLogisticElementsSelectOptionsBySubtypes(
    await apiConfigServicioCapacidadGET(serviceTypes).then(response => {
      return mapConfigOperationBalanceListFromApi(response).map(item => item.logisticElementSubType)
    }),
    getActiveBalanceZone()?.id
  )
}

export const modifyAllocation = async (item: AllocationModel): Promise<boolean> => {
  return await apiRepartosPATCH(mapAllocationPATCH(item))
    .then(() => {
      item.operationState = EnumOperationState.PendienteRenvio
      patchStateOperation(mapCapacityStatePATCH(item))
        .then(() => true)
        .catch(() => {
          notifyError(i18n.t('capacity:notifications.error.edit'))
        })
      return true
    })
    .catch(() => {
      notifyError(i18n.t('capacity:notifications.error.editAllocations'))
      return false
    })
}

export const canEditAllocation = (date: Date, lockState: EnumBloqueoCapacidad): boolean => {
  if (lockState === EnumBloqueoCapacidad.BloqueadoUsuario) {
    return false
  } else if (
    lockState === EnumBloqueoCapacidad.BloqueadoBundle &&
    moment(date).isBefore(moment(), 'days')
  ) {
    return false
  }
  return true
}

//#region Capacity global actions

export const filterCapacityApplicableActionsConfiguration = (
  item: CapacityModel,
  configurations: Array<DataTableActionConfiguration>
): Array<DataTableActionConfiguration> => {
  let applicableActionsConfiguration: Array<DataTableActionConfiguration> = []
  configurations.forEach(configuration => {
    switch (configuration.icon) {
      case ActionIcons.LOCK: {
        if (item.lockState === EnumBloqueoCapacidad.BloqueadoUsuario) {
          applicableActionsConfiguration.push(configuration)
        }
        break
      }
      case ActionIcons.UNLOCK: {
        if (item.lockState === EnumBloqueoCapacidad.Libre) {
          applicableActionsConfiguration.push(configuration)
        }
        break
      }
      case ActionIcons.LOCK_DISABLED: {
        if (item.lockState === EnumBloqueoCapacidad.BloqueadoBundle) {
          applicableActionsConfiguration.push(configuration)
        }
        break
      }
    }
  })

  return applicableActionsConfiguration
}

export const getGlobalActionsConfiguration = (
  hasPermission: boolean
): Array<DataTableActionConfiguration> => {
  return [
    getAllocationLockAction(hasPermission),
    getAllocationUnlockAction(hasPermission),
    getAllocationLockedAction(hasPermission)
  ]
}

const getAllocationLockedAction = (hasPermission: boolean): DataTableActionConfiguration => {
  return {
    icon: ActionIcons.LOCK_DISABLED,
    noHandlerTooltip: 'Bloqueado',
    hasPermission: hasPermission
  }
}

const getAllocationLockAction = (hasPermission: boolean): DataTableActionConfiguration => {
  return {
    icon: ActionIcons.LOCK,
    tooltip: i18n.t('translation:pages.allocations.dataTable.tooltips.lock'),
    noHandlerTooltip: i18n.t('translation:pages.allocations.dataTable.tooltips.lockDisabled'),
    hasPermission: hasPermission
  }
}

const getAllocationUnlockAction = (hasPermission: boolean): DataTableActionConfiguration => {
  return {
    icon: ActionIcons.UNLOCK,
    tooltip: i18n.t('translation:pages.allocations.dataTable.tooltips.unlock'),
    noHandlerTooltip: i18n.t('translation:pages.allocations.dataTable.tooltips.unlockDisabled'),
    handler: undefined,
    hasPermission: hasPermission
  }
}

//#endregion

//#region Capacity secondary actions

export const getSecondaryActionsConfiguration = (): Array<DataTableActionConfiguration> => {
  return [getAllocationBundleAction()]
}

const getAllocationBundleAction = (): DataTableActionConfiguration => {
  return {
    icon: ActionIcons.BUNDLE
  }
}

export const filterCapacityApplicableSecondaryActionsConfiguration = (
  item: CapacityModel,
  configurations: Array<DataTableActionConfiguration>
): Array<DataTableActionConfiguration> => {
  let applicableActionsConfiguration: Array<DataTableActionConfiguration> = []
  configurations.forEach(configuration => {
    switch (configuration.icon) {
      case ActionIcons.BUNDLE: {
        if (item.lockState === EnumBloqueoCapacidad.BloqueadoBundle) {
          configuration.tooltip = i18n.t('capacity:dataTable.tooltips.bundled', {
            parentId: item.parentProduct
          })
          configuration.noHandlerTooltip = i18n.t('capacity:dataTable.tooltips.bundled', {
            parentId: item.parentProduct
          })
          applicableActionsConfiguration.push(configuration)
        }
        break
      }
    }
  })

  return applicableActionsConfiguration
}

//#endregion

//#region Allocation global actions

export const filterApplicableActionsConfiguration = (
  item: AllocationModel,
  configurations: Array<DataTableActionConfiguration>
): Array<DataTableActionConfiguration> => {
  let applicableActionsConfiguration: Array<DataTableActionConfiguration> = []
  configurations.forEach(configuration => {
    switch (configuration.icon) {
      case ActionIcons.WARNING: {
        if (
          ((item.quantitySent && item.quantity !== item.quantitySent) ||
            (item.quantityTSO && item.quantity !== item.quantityTSO)) &&
          (item.operationState === EnumOperationState.PendienteRenvio ||
            item.operationState === EnumOperationState.Borrador)
        ) {
          applicableActionsConfiguration.push(configuration)
        }
        break
      }
      case ActionIcons.ERROR: {
        if (
          ((item.quantitySent && item.quantity !== item.quantitySent) ||
            (item.quantityTSO && item.quantity !== item.quantityTSO)) &&
          (item.operationState === EnumOperationState.Enviada ||
            item.operationState === EnumOperationState.Rechazado ||
            item.operationState === EnumOperationState.EnviadoKO)
        ) {
          applicableActionsConfiguration.push(configuration)
        }
        break
      }
    }
  })
  return applicableActionsConfiguration
}

export const getGlobalAllocationActionsConfiguration = (): Array<DataTableActionConfiguration> => {
  return [getQuantityDisagreementAction(), getStateDisagreementAction()]
}

const getQuantityDisagreementAction = (): DataTableActionConfiguration => {
  return {
    icon: ActionIcons.WARNING,
    tooltip: i18n.t('capacity:dataTable.tooltips.quantityDisagreement')
  }
}

const getStateDisagreementAction = (): DataTableActionConfiguration => {
  return {
    icon: ActionIcons.ERROR,
    tooltip: i18n.t('capacity:dataTable.tooltips.stateDisagreement')
  }
}

//#endregion
